import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TextField,
  CircularProgress
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { vendorProfileService, VendorProfile } from '../../services/vendorProfileService';

interface LegalInfoEditPopupProps {
  open: boolean;
  onClose: () => void;
  onUpdate: (updatedData: Partial<VendorProfile>) => void;
  vendorProfile: VendorProfile | null;
}

const LegalInfoEditPopup: React.FC<LegalInfoEditPopupProps> = ({
  open,
  onClose,
  onUpdate,
  vendorProfile
}) => {
  const [registeredName, setRegisteredName] = useState('');
  const [tradeLicenseNumber, setTradeLicenseNumber] = useState('');
  const [commercialRegistrationNo, setCommercialRegistrationNo] = useState('');
  const [registeredAddress, setRegisteredAddress] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Initialize form with current vendor data
  useEffect(() => {
    if (vendorProfile) {
      setRegisteredName(vendorProfile.registeredName || '');
      setTradeLicenseNumber(vendorProfile.tradeLicenseNumber || '');
      setCommercialRegistrationNo(vendorProfile.commercialRegistrationNo || '');
      setRegisteredAddress(vendorProfile.registeredAddress || '');
    }
  }, [vendorProfile, open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!vendorProfile) {
      toast.error('No vendor session found. Please try again.');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await vendorProfileService.editProfile(vendorProfile.vendorId, {
        registeredName: registeredName.trim(),
        tradeLicenseNumber: tradeLicenseNumber.trim(),
        commercialRegistrationNo: commercialRegistrationNo.trim(),
        registeredAddress: registeredAddress.trim(),
      });

      if (response.success) {
        toast.success('Legal information updated successfully!');
        onUpdate({
          registeredName: registeredName.trim(),
          tradeLicenseNumber: tradeLicenseNumber.trim(),
          commercialRegistrationNo: commercialRegistrationNo.trim(),
          registeredAddress: registeredAddress.trim(),
        });
        handleClose();
      } else {
        toast.error(response.message || 'Failed to update legal information');
      }
    } catch (error: any) {
      console.error('Error updating legal info:', error);
      const errorMessage = error.response?.data?.error || 
                          error.response?.data?.message || 
                          'Failed to update legal information. Please try again.';
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        pb: 1
      }}>
        <Typography variant="h6" sx={{ 
          fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
          fontWeight: 700,
          fontSize: '20px',
          color: '#181818'
        }}>
          Edit Legal Information
        </Typography>
        <IconButton onClick={handleClose} disabled={isSubmitting}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            py: 2
          }}
        >
          {/* Registered Name Field */}
          <TextField
            fullWidth
            placeholder="Registered name"
            value={registeredName}
            onChange={(e) => setRegisteredName(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#f6f8f8',
                borderRadius: '12px',
                height: '56px',
                '& fieldset': {
                  border: 'none'
                },
                '&:hover fieldset': {
                  border: 'none'
                },
                '&.Mui-focused fieldset': {
                  border: 'none'
                }
              },
              '& .MuiInputBase-input': {
                fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
                fontSize: '16px',
                color: registeredName ? '#181818' : '#93989c',
                '&::placeholder': {
                  color: '#93989c',
                  opacity: 1
                }
              }
            }}
          />

          {/* Trade License Number Field */}
          <TextField
            fullWidth
            placeholder="Trade license number"
            value={tradeLicenseNumber}
            onChange={(e) => setTradeLicenseNumber(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#f6f8f8',
                borderRadius: '12px',
                height: '56px',
                '& fieldset': {
                  border: 'none'
                },
                '&:hover fieldset': {
                  border: 'none'
                },
                '&.Mui-focused fieldset': {
                  border: 'none'
                }
              },
              '& .MuiInputBase-input': {
                fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
                fontSize: '16px',
                color: tradeLicenseNumber ? '#181818' : '#93989c',
                '&::placeholder': {
                  color: '#93989c',
                  opacity: 1
                }
              }
            }}
          />

          {/* Commercial Registration No Field */}
          <TextField
            fullWidth
            placeholder="Commercial Registration No"
            value={commercialRegistrationNo}
            onChange={(e) => setCommercialRegistrationNo(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#f6f8f8',
                borderRadius: '12px',
                height: '56px',
                '& fieldset': {
                  border: 'none'
                },
                '&:hover fieldset': {
                  border: 'none'
                },
                '&.Mui-focused fieldset': {
                  border: 'none'
                }
              },
              '& .MuiInputBase-input': {
                fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
                fontSize: '16px',
                color: commercialRegistrationNo ? '#181818' : '#93989c',
                '&::placeholder': {
                  color: '#93989c',
                  opacity: 1
                }
              }
            }}
          />

          {/* Registered Address Field */}
          <TextField
            fullWidth
            multiline
            rows={4}
            placeholder="Registered Address"
            value={registeredAddress}
            onChange={(e) => setRegisteredAddress(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#f6f8f8',
                borderRadius: '12px',
                '& fieldset': {
                  border: 'none'
                },
                '&:hover fieldset': {
                  border: 'none'
                },
                '&.Mui-focused fieldset': {
                  border: 'none'
                }
              },
              '& .MuiInputBase-input': {
                fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
                fontSize: '16px',
                color: registeredAddress ? '#181818' : '#93989c',
                '&::placeholder': {
                  color: '#93989c',
                  opacity: 1
                }
              }
            }}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            width: '100%'
          }}
        >
          {/* Update Button */}
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting}
            fullWidth
            sx={{
              height: '56px',
              backgroundColor: '#67314f',
              borderRadius: '16px',
              fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
              fontWeight: 700,
              fontSize: '16px',
              letterSpacing: '-0.32px',
              textTransform: 'none',
              color: 'white',
              '&:hover': {
                backgroundColor: '#5a2a42'
              },
              '&:disabled': {
                backgroundColor: '#93989c',
                color: 'white'
              }
            }}
          >
            {isSubmitting ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={20} sx={{ color: 'white' }} />
                Updating...
              </Box>
            ) : (
              'Update Legal Information'
            )}
          </Button>

          {/* Cancel Button */}
          <Button
            onClick={handleClose}
            disabled={isSubmitting}
            fullWidth
            sx={{
              height: '56px',
              backgroundColor: 'transparent',
              borderRadius: '16px',
              fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
              fontWeight: 700,
              fontSize: '16px',
              letterSpacing: '-0.32px',
              textTransform: 'none',
              color: '#67314f',
              '&:hover': {
                backgroundColor: 'rgba(103, 49, 79, 0.04)'
              }
            }}
          >
            Cancel
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default LegalInfoEditPopup;
